import { useCallback, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { FormikHelpers } from 'formik';

import { getDocumentName } from '@e-socna/data-layer';
import { buildPath } from '@e-socna/utils';

import { userFriendlyErrors } from '../../../utils/back-errors.util';
import { PATH_MISSION_ACCOUNTING_DEFAULT } from '../../../app/constants/paths.constant';
import { useMissionReadyContext } from '../../../contexts/MissionContext/hooks/useMissionReadyContext.hook';
import type { BackError } from '../../../utils/back-errors.util';

import { MissionPage_OneMission_QueryDocument } from '../../Mission/hooks/useFindOneMissionQuery.hook';

import { accountingInfoForm2ApiMapper } from '../mappers/accounting-info.form2api.mapper';
import { MISSION_SPACE_PARAM } from '../../../app/utils/mission-space.util';
import { useMissionSpace } from '../../../hooks/useMissionSpace.hook';
import { MessageList } from '../../../components/MessageList';

import { useInitAccountingInfoMutation } from './useInitAccountingInfoMutation.hook';
import type { AccountingInfo_Init_Result } from './useInitAccountingInfoMutation.hook';

export interface AccountingInfoFormValues {
  readonly workflowId: string;
}

export const useInitHandler = () => {
  const { folderId, missionId } = useMissionReadyContext();
  const missionSpace = useMissionSpace();

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { mutate: initAccountingInfoMutation } =
    useInitAccountingInfoMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onInit = useCallback(
    (handleSuccess?: () => void) =>
      (
        values: Readonly<AccountingInfoFormValues>,
        { setSubmitting }: Readonly<FormikHelpers<AccountingInfoFormValues>>,
      ) => {
        setIsSubmitting(true);

        initAccountingInfoMutation(
          {
            missionId,
            accountingInfo: accountingInfoForm2ApiMapper(values),
          },
          {
            onSuccess: (_: AccountingInfo_Init_Result) => {
              handleSuccess?.();
            },
            onError: (error: unknown) => {
              enqueueSnackbar(
                <MessageList
                  messages={userFriendlyErrors(error as BackError)}
                />,
                {
                  variant: 'error',
                },
              );
            },
            onSettled: () => {
              setIsSubmitting(false);
              setSubmitting(false);
            },
          },
        );
      },
    [enqueueSnackbar, initAccountingInfoMutation, missionId],
  );

  const handleSuccess = useCallback(() => {
    const path = buildPath(PATH_MISSION_ACCOUNTING_DEFAULT, {
      folderId,
      missionId,
      [MISSION_SPACE_PARAM]: missionSpace,
    });

    void queryClient.invalidateQueries({
      queryKey: [
        getDocumentName(MissionPage_OneMission_QueryDocument),
        { id: missionId },
      ],
    });

    enqueueSnackbar('La comptabilité a bien été initialisée.');

    void navigate(path);
  }, [
    enqueueSnackbar,
    folderId,
    missionId,
    missionSpace,
    navigate,
    queryClient,
  ]);

  return { handleSuccess, isSubmitting, onInit };
};
